import {
    ChangeDetectionStrategy,
    Component,
    computed,
    DestroyRef,
    effect,
    inject,
    untracked,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { BcdApplicationStatus } from 'app/core/enums/generated/BcdApplicationStatus';
import { InsulinDashboardService } from 'app/insulin-program/shared/insulin-dashboard.service';
import { InsulinHeaderFacilityService } from 'app/insulin-program/shared/insulin-header-facility.service';
import { statusLabel, statusSeverity } from 'app/insulin-program/shared/insulin-status';
import { LinkComponent } from 'app/shared/link/link.component';
import { InsulinQueueTab } from 'app/insulin-program/intake-queue/insulin-intake-queue.service';
import { TagModule } from 'primeng/tag';
import { ResizableWidgetComponent } from '../resizable-widget/resizable-widget.component';

/**
 * The Insulin applications waiting on a reviewer, oldest first.
 *
 * A prompt rather than a queue: it lists a few of them and hands off to the intake queue for the
 * actual work.
 *
 * Scoped to the facility selected in HOMER's header when it is one of the reader's Insulin
 * facilities, and to every facility they are associated with otherwise. The link into the queue names
 * the same facility, so the count here and the count you land on agree. Each row names its own
 * facility, which is how the mockup distinguishes them.
 *
 * Everything on screen is decided by the server - the counts, which rows, the ages, and whether this
 * user may see any of it at all. The widget renders `isEnabled: false` as nothing, which is the same
 * answer the server gives whether the reason is module access, facility association or a facility
 * that has never switched the widgets on. Nothing is inferred here from an empty list, because
 * "nobody enabled these" and "there is no work today" must not look the same.
 */
@Component({
    selector: 'pcg-insulin-requires-review-widget',
    imports: [
        ResizableWidgetComponent
        , LinkComponent
        , TagModule
    ],
    templateUrl: './insulin-requires-review-widget.component.html',
    styleUrl: './insulin-requires-review-widget.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InsulinRequiresReviewWidgetComponent {

    // #region Injections
    private readonly dashboardService = inject(InsulinDashboardService);
    private readonly header = inject(InsulinHeaderFacilityService);
    private readonly destroyRef = inject(DestroyRef);
    // #endregion

    // #region State (read from the shared service, so all three Insulin widgets draw the same moment)
    readonly isLoading = this.dashboardService.isLoading;
    readonly hasError = this.dashboardService.hasError;

    private readonly dashboard = this.dashboardService.dashboard;

    /** Whether there is anything to show at all. False also covers "not loaded yet". */
    readonly isEnabled = computed(() => this.dashboard()?.isEnabled === true);
    readonly unavailableMessage = this.dashboardService.unavailableMessage;

    readonly count = computed(() => this.dashboard()?.requiresReview ?? 0);

    readonly rows = computed(() => this.dashboard()?.requiresReviewRows ?? []);
    // #endregion

    // #region Status chip
    // Every row in this widget is Requires Review - that is the query - so the status is a constant
    // rather than a field on the row. Named here and run through the shared helpers anyway, so the
    // label and the colour come from the same place as every other Insulin screen. If the sample is
    // ever widened to carry mixed statuses, this becomes `row.status` and nothing else changes.
    readonly reviewStatus = BcdApplicationStatus.RequiresReview;

    readonly statusLabel = statusLabel;
    readonly statusSeverity = statusSeverity;
    // #endregion

    // The oldest-waiting figure the server still sends (oldestRequiresReviewDays) is not shown here.
    // Each row already carries its own age, and the list is oldest first, so the top row states the
    // same thing without a separate banner repeating it. The summary widget shows it as the Requires
    // Review card's subtitle, which is where the mockup puts it.

    // Asks again whenever the header facility changes. The figures follow it - that facility's
    // when it is one of the reader's Insulin facilities, all of theirs otherwise - which is the same
    // default the intake queue these link into starts from (InsulinHeaderFacilityService). Waits
    // until the header facility is known, so the first request is already the right one.
    private readonly followHeaderFacility = effect(() => {
        if (!this.header.ready()) { return; }
        this.header.facilityId();
        untracked(() => this.load());
    });

    /**
     * The intake queue on its Requires Review tab with no other filter, which is the list the rows
     * above are taken from. `clear=1` because the queue keeps its filters between visits: a filter
     * left from the last one would open this on a shorter list than the widget promises, or on a
     * different tab altogether (see the queue summary widget). The facility the rows were read for
     * goes with it.
     */
    readonly queueUrl = computed(() => {
        const facilityId = this.header.facilityId();
        const facility = facilityId == null ? '' : `&facilityId=${facilityId}`;
        return `insulin-program/intake-queue/list?clear=1&tab=${InsulinQueueTab.RequiresReview}${facility}`;
    });

    /** The review screen re-checks module and facility access against the record, not against this id. */
    reviewUrl(bcdProgramApplicationId: number): string {
        return `insulin-program/intake-queue/review/${bcdProgramApplicationId}`;
    }

    private load(): void {
        this.dashboardService
            .refresh()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe();
    }
}
